import React, { useRef, useEffect, useState, memo } from 'react';
import * as THREE from 'three';
import { useModelLoader } from '../../hooks/useModelLoader';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { usePreloading, useActiveScreen, useSound } from '../../contexts/SiteContext';
import useVideoTexture from './VideoTexture';
import sceneConfig from '../../config/sceneConfig';
import { logger } from '../../utils/logger';
import { updateVideoPath } from '../../utils/videoPath';
import { getImagePath } from '../../utils/imagePath';
import { isTvInteractiveElement, setCanvasPointer, checkAllObjectsUnderCursor, getObjectHierarchy, isTvButton1, isTvButton2, isTvScreenDisplay } from '../../utils/interactive3DHelpers';

const TV_FADE_IN_DURATION = 500; // 0.5 seconds
const TV_FADE_OUT_DURATION = 2000; // ~2 seconds for graceful fade out
const TV_CHANNEL_TRANSITION_DURATION = 300; // 300ms for channel transition

const TvScene = ({ onSceneClick, onButton1Click, onButton2Click, offsetPosition }) => {
  const { gltf, isLoading, error } = useModelLoader('/models/tv.glb', 'tv-scene');
  const { isPreloading } = usePreloading();
  const { activeScreen } = useActiveScreen();
  const { isSoundOn } = useSound();
  const {
    registerSceneObject,
    getSceneObject,
    setModelLoaded,
    readyToPosition,
    assetQuality
  } = useCanvasContext();

  // Only play audio when user is on the TV scene and sound is on
  const isTvSceneActive = activeScreen === 'tv';

  const sceneRef = useRef();
  const [sceneObject, setSceneObject] = useState(null);
  const [isModelProcessed, setIsModelProcessed] = useState(false);
  const [screenMesh, setScreenMesh] = useState(null);
  const materialAppliedRef = useRef(false);
  const [hasVideoStarted, setHasVideoStarted] = useState(false);

  // Audio fade state
  // Initialize with target volume based on initial state
  const initialVolume = (isTvSceneActive && isSoundOn && hasVideoStarted) ? 1.0 : 0.0;
  const [fadeVolume, setFadeVolume] = useState(initialVolume);
  const fadeAnimationRef = useRef(null);
  const targetVolumeRef = useRef(initialVolume);
  const currentVolumeRef = useRef(initialVolume);
  // Ref to track the latest fadeVolume state value for use in cleanup
  const fadeVolumeRef = useRef(initialVolume);
  // Cancellation token to ensure animations stop immediately
  const animationGenerationRef = useRef(0);
  // Throttle state updates to prevent excessive re-renders
  const lastStateUpdateRef = useRef(0);
  const STATE_UPDATE_THROTTLE = 16; // ~60fps max for state updates
  // Ref to store video element for direct volume updates
  const videoElementRef = useRef(null);

  // Track if video has been played (to only start on first scroll)
  const hasVideoPlayedRef = useRef(false);
  // Track if video has completed one full playthrough
  const hasVideoCompletedRef = useRef(false);
  const videoEndListenerRef = useRef(null);
  const previousVideoTimeRef = useRef(0);
  const channelTransitionTimeoutRef = useRef(null);
  const videoPauseTimeoutRef = useRef(null);

  // State for video source (managed internally in TvScene)
  // Use quality-aware video path based on connection speed
  const baseVideoPath = '/videos/waiting-on-lettuce.mp4';
  const qualityAwareVideoPath = updateVideoPath(baseVideoPath, assetQuality);
  const [videoSrc, setVideoSrc] = useState(qualityAwareVideoPath);

  // Update video source when quality changes
  useEffect(() => {
    const newQualityPath = updateVideoPath(baseVideoPath, assetQuality);
    // Only update if the path actually changed and we're currently showing video
    if (newQualityPath !== videoSrc && videoSrc && videoSrc !== null && videoSrc.startsWith('/videos/')) {
      logger.debug('Video quality changed, updating video source', {
        oldQuality: videoSrc,
        newQuality: newQualityPath,
        assetQuality
      });
      setVideoSrc(newQualityPath);
    }
  }, [assetQuality, baseVideoPath, videoSrc]);

  // State to control placeholder image visibility during transitions
  const [showPlaceholderImage, setShowPlaceholderImage] = useState(true);

  // Helper function to change TV channel with transition effect
  const setTvChannel = (newVideoSrc) => {
    // Clear any existing transition timeout
    if (channelTransitionTimeoutRef.current) {
      clearTimeout(channelTransitionTimeoutRef.current);
      channelTransitionTimeoutRef.current = null;
    }

    // Hide placeholder image during transition
    setShowPlaceholderImage(false);
    setVideoSrc(null);
    logger.debug('TV channel transition started - hiding placeholder');

    // After transition duration, set new video source and restore placeholder
    channelTransitionTimeoutRef.current = setTimeout(() => {
      // Update video source directly
      setVideoSrc(newVideoSrc);

      // Restore placeholder image
      setShowPlaceholderImage(true);

      logger.debug('TV channel transition complete - restored placeholder', {
        newVideoSrc
      });
      channelTransitionTimeoutRef.current = null;
    }, TV_CHANNEL_TRANSITION_DURATION);
  };

  // Helper function to get texture scaling values
  // Uses the exact same values as the static shader texture (no scaling calculation)
  // The static shader uses fixed values that fill the screen, so we use the same fixed values
  const getTextureScaling = () => {
    // Exact same values as createStaticShaderMaterial defaults:
    // textureRepeat = 4.6, textureOffset = { x: -3.13, y: -1.05 }
    return {
      repeat: 4.6, // Same as static shader textureRepeat
      offset: { x: -3.13, y: -1.05 } // Same as static shader textureOffset
    };
  };

  // Keep fadeVolumeRef in sync with fadeVolume state
  useEffect(() => {
    fadeVolumeRef.current = fadeVolume;
  }, [fadeVolume]);

  // Audio fade effect - smoothly transition volume when switching screens
  useEffect(() => {
    // Determine target volume: 1.0 if TV scene is active and sound is on
    // Check if video is actually playing or has been started, not just hasVideoStarted state
    // This handles the case where we return to TV screen before pause timeout fires
    const video = videoElementRef.current;
    const isVideoPlaying = video && !video.paused && video.readyState >= 3;
    const shouldPlayAudio = isTvSceneActive && isSoundOn && (hasVideoStarted || isVideoPlaying);
    const targetVolume = shouldPlayAudio ? 1.0 : 0.0;
    targetVolumeRef.current = targetVolume;

    // Increment generation counter to invalidate any running animations
    animationGenerationRef.current += 1;
    const currentGeneration = animationGenerationRef.current;

    // Cancel any existing fade animation and capture current volume state
    if (fadeAnimationRef.current) {
      cancelAnimationFrame(fadeAnimationRef.current);
      fadeAnimationRef.current = null;
      // Sync currentVolumeRef with the actual current volume state
      // Prefer fadeVolumeRef, but also check actual video volume as fallback
      const video = videoElementRef.current;
      if (video && Math.abs(video.volume - fadeVolumeRef.current) > 0.05) {
        // Video volume differs significantly, use video volume as source of truth
        currentVolumeRef.current = video.volume;
        fadeVolumeRef.current = video.volume;
      } else {
        // Use fadeVolumeRef as source of truth
        currentVolumeRef.current = fadeVolumeRef.current;
      }
    }

    // If already at target, no need to fade - but ensure state matches
    if (Math.abs(currentVolumeRef.current - targetVolume) < 0.01) {
      // Ensure state is synced even if we're not fading
      if (Math.abs(fadeVolumeRef.current - targetVolume) >= 0.01) {
        currentVolumeRef.current = targetVolume;
        fadeVolumeRef.current = targetVolume;
        setFadeVolume(targetVolume);
      }
      return;
    }

    const startVolume = currentVolumeRef.current;
    const volumeDifference = targetVolume - startVolume;
    const startTime = performance.now();
    // Fade duration in milliseconds (longer fade when turning audio off)
    const fadeDuration = targetVolume < startVolume
      ? TV_FADE_OUT_DURATION
      : TV_FADE_IN_DURATION;

    const animateFade = (currentTime) => {
      // Check if this animation generation is still valid (not canceled)
      if (currentGeneration !== animationGenerationRef.current) {
        // This animation was canceled, stop immediately and sync volume
        currentVolumeRef.current = fadeVolumeRef.current;
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / fadeDuration, 1.0);

      // Use easing function for smoother fade (ease-in-out)
      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const newVolume = startVolume + volumeDifference * easedProgress;
      currentVolumeRef.current = newVolume;
      fadeVolumeRef.current = newVolume; // Keep ref in sync

      // Throttle state updates to prevent excessive re-renders
      const now = performance.now();
      const shouldUpdateState = progress >= 1.0 || (now - lastStateUpdateRef.current) >= STATE_UPDATE_THROTTLE;

      if (shouldUpdateState) {
        setFadeVolume(newVolume);
        lastStateUpdateRef.current = now;
      }

      // Also update video volume directly if available (more responsive)
      const video = videoElementRef.current;
      if (video && Math.abs(video.volume - newVolume) > 0.01) {
        video.volume = Math.max(0, Math.min(1, newVolume));
      }

      if (progress < 1.0) {
        fadeAnimationRef.current = requestAnimationFrame(animateFade);
      } else {
        // Ensure we end exactly at target volume
        currentVolumeRef.current = targetVolume;
        fadeVolumeRef.current = targetVolume; // Keep ref in sync
        setFadeVolume(targetVolume);

        // Update video volume directly
        if (video) {
          video.volume = Math.max(0, Math.min(1, targetVolume));
        }

        fadeAnimationRef.current = null;
      }
    };

    fadeAnimationRef.current = requestAnimationFrame(animateFade);

    // Cleanup on unmount or when dependencies change
    return () => {
      // Increment generation to cancel this animation
      animationGenerationRef.current += 1;

      if (fadeAnimationRef.current) {
        cancelAnimationFrame(fadeAnimationRef.current);
        // Capture current volume state before cleanup using ref
        currentVolumeRef.current = fadeVolumeRef.current;
        fadeAnimationRef.current = null;
      }
    };
  }, [isTvSceneActive, isSoundOn, hasVideoStarted]);

  // Initialize video texture
  // Keep muted false since we're controlling volume via fade
  // Disable autoPlay so video only starts when user scrolls to TV screen
  // Use dynamic quality selection for placeholder from assetQuality (via Context)
  const videoTexture = useVideoTexture({
    videoSrc: videoSrc,
    placeholderImage: getImagePath('tv-placeholder.png', assetQuality),
    showPlaceholderImage: showPlaceholderImage, // Control placeholder visibility during transitions
    muted: false, // Keep unmuted, control via volume fade
    volume: fadeVolume, // Use faded volume
    autoPlay: false, // Don't auto-play - wait for user to scroll to TV screen
    onVideoLoad: (video) => {
      logger.debug('TV video loaded and ready to play');
    },
    onVideoError: (error) => {
      console.warn('TV video failed to load, using placeholder:', error);
    }
  });

  // Keep video element ref in sync with videoTexture
  useEffect(() => {
    videoElementRef.current = videoTexture.video;
  }, [videoTexture.video]);

  // Model loading and processing
  // Register scene object immediately when gltf loads, before video texture processing
  // This ensures labels can find the scene object even if video texture loading is delayed
  useEffect(() => {
    if (gltf && gltf.scene) {
      // Always register the scene immediately so labels can find it
      // The main processing effect will handle scaling, positioning, and material setup
      registerSceneObject('tv-scene', gltf.scene);
      logger.debug('Registered TV scene object for labels (early registration)');
      // Also set the scene object reference immediately so processing can use it
      setSceneObject(gltf.scene);
    }
  }, [gltf, registerSceneObject]);

  useEffect(() => {
    if (gltf && gltf.scene && !isModelProcessed) {
      // Always use gltf.scene directly - it's the same object we registered in the early registration effect
      // This ensures we're working with the exact object reference that's registered for labels
      const workingScene = gltf.scene;

      // Check if we already have processed scene in the global context
      // Note: This check might return null initially due to React state batching, but that's OK
      // We'll process and register it anyway
      const existingSceneObject = getSceneObject('tv-scene');

      if (existingSceneObject && existingSceneObject !== workingScene && existingSceneObject !== gltf.scene) {
        logger.debug('Using cached TV scene from global context');
        setSceneObject(existingSceneObject);
        setIsModelProcessed(true);
        return;
      }

      // Process the model for the first time

      // Adjust scale and position for the TV model
      // Get bounding box to understand the model size
      new THREE.Box3().setFromObject(workingScene);

      // Use configuration for TV positioning
      const tvConfig = sceneConfig.modelPositions.tv;
      workingScene.scale.setScalar(tvConfig.scale);

      // Use loading position during preload, final position with offset otherwise
      // All models use the same preload position for consistent viewing
      // During preload, ignore offsetPosition to keep objects at loading position
      const position = isPreloading ? [0, -65, -150] : (offsetPosition || tvConfig.position);
      workingScene.position.set(...position);
      workingScene.rotation.set(...tvConfig.rotation);


      // Defer heavy mesh processing to prevent blocking the main thread
      const processMeshes = () => {
        workingScene.traverse((child) => {
          if (child.isMesh) {
            try {
              // Check if this is the exact "screen-display" mesh
              const isScreenMesh = child.name.toLowerCase() === 'screen-display';

              if (isScreenMesh) {

                // Debug mesh geometry
                const geometry = child.geometry;
                if (geometry.attributes.uv) {
                } else {
                }

                setScreenMesh(child);

                // Apply material to screen mesh only if not already applied
                // Check if material is already a video texture material to avoid re-applying
                const hasVideoMaterial = child.material &&
                  child.material.map &&
                  child.material.map === videoTexture.texture;

                // Prioritize video texture over static mode when video is ready
                if (videoTexture.texture && videoTexture.isVideoLoaded && videoTexture.video && !hasVideoMaterial) {
                  // Verify video has actual frame data before applying texture
                  const video = videoTexture.video;
                  // Double-check video has frame data - sometimes isVideoLoaded is true but frames aren't ready yet
                  if (!video || video.videoWidth === 0 || video.videoHeight === 0 || video.readyState < 3) {
                    logger.debug('Video texture not ready - video has no frame data yet', {
                      hasVideo: !!video,
                      videoWidth: video?.videoWidth,
                      videoHeight: video?.videoHeight,
                      readyState: video?.readyState
                    });
                    return;
                  }

                  // Use the original texture directly (don't clone - cloning breaks video connection)
                  const meshTexture = videoTexture.texture;

                  // Reset texture properties
                  meshTexture.offset.set(0, 0);
                  meshTexture.repeat.set(1, 1);
                  meshTexture.wrapS = THREE.ClampToEdgeWrapping;
                  meshTexture.wrapT = THREE.ClampToEdgeWrapping;
                  meshTexture.flipY = false;

                  // Use the same fixed values as the static shader texture
                  // This ensures the video fills the screen the same way as the static
                  const scaling = getTextureScaling();

                  meshTexture.repeat.set(scaling.repeat, scaling.repeat);
                  meshTexture.offset.set(scaling.offset.x, scaling.offset.y);

                  logger.debug('Applied texture scaling (matching static shader):', {
                    videoSize: `${video.videoWidth}x${video.videoHeight}`,
                    repeat: scaling.repeat,
                    offset: scaling.offset
                  });

                  // Create new material with video texture (use original texture, not cloned)
                  // Use white color (1,1,1) to show video's natural colors without any tinting
                  const screenMaterial = new THREE.MeshBasicMaterial({
                    map: meshTexture,
                    color: new THREE.Color(1, 1, 1), // White color to show video's natural colors
                    transparent: false,
                    side: THREE.FrontSide // Only apply to front side to avoid double application
                  });

                  child.material = screenMaterial;
                  child.material.needsUpdate = true;
                  logger.debug(`✅ Applied video texture to TV screen: ${child.name} with tall mesh fitting`);
                } else if (hasVideoMaterial) {
                  // Material already applied, just ensure it's up to date
                  child.material.needsUpdate = true;
                } else if (videoTexture.isStaticMode && videoTexture.shaderMaterial) {
                  // Fall back to static shader material if video is not ready
                  child.material = videoTexture.shaderMaterial;
                  child.material.needsUpdate = true;
                  logger.debug('Applied static shader material as fallback during initial processing');
                } else {
                  logger.debug(`Video texture not ready yet for screen: ${child.name}`, {
                    hasTexture: !!videoTexture.texture,
                    isVideoLoaded: videoTexture.isVideoLoaded,
                    hasVideo: !!videoTexture.video,
                    isStaticMode: videoTexture.isStaticMode
                  });
                }
              }

              // Ensure material properties are set
              if (child.material) {
                child.material.needsUpdate = true;
                child.material.transparent = false;
                child.material.opacity = 1.0;
              }

              // Enable shadow casting and receiving
              child.castShadow = true;
              child.receiveShadow = true;

              // Add click handlers
              child.userData.originalName = child.name;
              child.userData.originalPosition = child.position.clone();
              child.userData.isClickable = true;

              // Mark TV interactive elements specifically
              const normalizedName = child.name.toLowerCase();
              const normalizedOrig = (child.userData.originalName || '').toLowerCase();
              if (normalizedName.includes('button') || normalizedName.includes('buton') ||
                normalizedName.includes('screen-display') || normalizedName.includes('screendisplay') ||
                normalizedOrig.includes('button') || normalizedOrig.includes('buton') ||
                normalizedOrig.includes('screen-display') || normalizedOrig.includes('screendisplay')) {
                child.userData.isTvInteractive = true;
              }

              // Ensure meshes can receive pointer events
              // In React Three Fiber, meshes need to be set up to receive pointer events
              if (child.isMesh) {
                // Make sure the mesh is set up for pointer events
                child.matrixAutoUpdate = true;
                // Ensure the mesh can receive pointer events
                // This is important for React Three Fiber to detect hover
                if (child.userData.isTvInteractive) {
                  // Mark interactive meshes explicitly for better event handling
                  // Ensure the mesh can be raycasted for pointer events
                  if (!child.raycast) {
                    child.raycast = THREE.Mesh.prototype.raycast;
                  }
                  // Make sure the mesh is visible and can receive events
                  child.visible = true;
                }
              }
            } catch (error) {
              console.warn('Error processing TV mesh:', child.name, error);
            }
          }
        });
      };

      // Defer mesh processing to prevent blocking the main thread
      setTimeout(processMeshes, 0);

      // Register scene in global context for reuse (if not already registered)
      // The early registration effect should have already registered it, but ensure it's registered
      registerSceneObject('tv-scene', workingScene);

      // Set reference for current use
      setSceneObject(workingScene);

      setIsModelProcessed(true);

      // Don't signal loaded yet - wait for readyToPosition signal
    }
  }, [gltf, registerSceneObject, getSceneObject, isModelProcessed, isPreloading, setModelLoaded, offsetPosition, videoTexture.isStaticMode, videoTexture.shaderMaterial, videoTexture.texture, videoTexture.isVideoLoaded, videoTexture.video, sceneObject]);

  // Move to final position when preloader signals ready
  useEffect(() => {
    if (sceneObject && readyToPosition) {
      const tvConfig = sceneConfig.modelPositions.tv;
      const targetPos = new THREE.Vector3(...(offsetPosition || tvConfig.position));
      const targetRot = new THREE.Euler(...tvConfig.rotation);
      sceneObject.position.copy(targetPos);
      sceneObject.rotation.copy(targetRot);

      // Signal that TV model is loaded and positioned (this also resolves the promise)
      setModelLoaded('tv', true);
    }
  }, [sceneObject, readyToPosition, setModelLoaded, offsetPosition]);

  // Update position when offset changes (after initial load)
  // Don't update during preload - keep objects at loading position
  useEffect(() => {
    if (sceneObject && readyToPosition && offsetPosition && !isPreloading) {
      const targetPos = new THREE.Vector3(...offsetPosition);
      sceneObject.position.copy(targetPos);
    }
  }, [sceneObject, readyToPosition, offsetPosition, isPreloading]);

  // Apply material to screen-display mesh when available
  useEffect(() => {
    if (!screenMesh) {
      materialAppliedRef.current = false;
      return;
    }

    // Check if material is already a video texture material to avoid re-applying on resize
    const hasVideoMaterial = screenMesh.material &&
      screenMesh.material.map &&
      screenMesh.material.map === videoTexture.texture;

    // If material is already applied and matches, don't re-apply
    if (hasVideoMaterial && materialAppliedRef.current) {
      return;
    }

    // Reset flag if material doesn't match (video source changed or material was removed)
    if (!hasVideoMaterial && materialAppliedRef.current) {
      materialAppliedRef.current = false;
    }

    // Prioritize video texture over static mode when video is ready
    if (videoTexture.texture && videoTexture.isVideoLoaded && videoTexture.video && !hasVideoMaterial) {
      // Verify video has actual frame data before applying texture
      const video = videoTexture.video;
      // Double-check video has frame data - sometimes isVideoLoaded is true but frames aren't ready yet
      if (!video || video.videoWidth === 0 || video.videoHeight === 0 || video.readyState < 3) {
        logger.debug('Video texture not ready - video has no frame data yet', {
          hasVideo: !!video,
          videoWidth: video?.videoWidth,
          videoHeight: video?.videoHeight,
          readyState: video?.readyState
        });
        return;
      }

      // Use the original texture directly (don't clone - cloning breaks video connection)
      const meshTexture = videoTexture.texture;

      // Based on the mesh dimensions (very tall), try a different approach
      // The mesh appears to be oriented vertically, so let's adjust accordingly
      meshTexture.offset.set(0, 0);
      meshTexture.repeat.set(1, 1);
      meshTexture.wrapS = THREE.ClampToEdgeWrapping;
      meshTexture.wrapT = THREE.ClampToEdgeWrapping;
      meshTexture.flipY = false;

      // Use the same fixed values as the static shader texture
      // This ensures the video fills the screen the same way as the static
      const scaling = getTextureScaling();

      meshTexture.repeat.set(scaling.repeat, scaling.repeat);
      meshTexture.offset.set(scaling.offset.x, scaling.offset.y);

      logger.debug('Applied texture scaling in useEffect (matching static shader):', {
        videoSize: `${video.videoWidth}x${video.videoHeight}`,
        repeat: scaling.repeat,
        offset: scaling.offset
      });

      // Create new material with video texture (use original texture, not cloned)
      // Use white color (1,1,1) to show video's natural colors without any tinting
      const screenMaterial = new THREE.MeshBasicMaterial({
        map: meshTexture,
        color: new THREE.Color(1, 1, 1), // White color to show video's natural colors
        transparent: false,
        side: THREE.FrontSide // Only apply to front side to avoid double application
      });

      screenMesh.material = screenMaterial;
      screenMesh.material.needsUpdate = true;
      materialAppliedRef.current = true;

      logger.debug(`✅ Video texture applied to ${screenMesh.name} with single application`);
      logger.debug('Final texture settings:', {
        video: video ? `${video.videoWidth}x${video.videoHeight}` : 'no video',
        aspectRatio: video ? video.videoWidth / video.videoHeight : 1,
        offset: { x: meshTexture.offset.x, y: meshTexture.offset.y },
        repeat: { x: meshTexture.repeat.x, y: meshTexture.repeat.y },
        flipY: meshTexture.flipY
      });
      return; // Exit early after applying video texture
    }

    // If video material is already applied, just update it
    if (hasVideoMaterial) {
      return; // Don't update needsUpdate every frame
    }

    // Fall back to static mode if video is not ready
    if (videoTexture.isStaticMode && videoTexture.shaderMaterial && !materialAppliedRef.current) {
      screenMesh.material = videoTexture.shaderMaterial;
      screenMesh.material.needsUpdate = true;
      materialAppliedRef.current = true;
      logger.debug('Applied static shader material as fallback');
    }
  }, [screenMesh, videoTexture.isStaticMode, videoTexture.isVideoLoaded, videoTexture.texture, videoTexture.video, videoTexture.shaderMaterial]);

  // Control video playback based on TV screen visibility
  useEffect(() => {
    const video = videoTexture.video;
    if (!video) return;

    // Only proceed if video is ready to play and loaded
    if (video.readyState < 3 || !videoTexture.isVideoLoaded) {
      return;
    }

    if (isTvSceneActive) {
      // User has scrolled to TV screen - cancel any pending pause timeout
      if (videoPauseTimeoutRef.current) {
        clearTimeout(videoPauseTimeoutRef.current);
        videoPauseTimeoutRef.current = null;
        logger.debug('Cancelled pending video pause timeout');
      }

      // If video is already playing (pause timeout was cancelled), ensure hasVideoStarted is true
      if (!video.paused && !hasVideoStarted) {
        setHasVideoStarted(true);
        logger.debug('TV video was already playing, updated hasVideoStarted');
      }

      // Start playing if not already playing
      if (video.paused) {
        video.play().then(() => {
          hasVideoPlayedRef.current = true;
          setHasVideoStarted(true);
          logger.debug('TV video started playing on scroll to TV screen');
        }).catch(err => {
          logger.debug('Could not play video:', err);
        });
      }
    } else {
      // User has navigated away from TV screen - pause the video after fade out
      if (!video.paused) {
        // Clear any existing pause timeout
        if (videoPauseTimeoutRef.current) {
          clearTimeout(videoPauseTimeoutRef.current);
          videoPauseTimeoutRef.current = null;
        }

        // Set new pause timeout
        videoPauseTimeoutRef.current = setTimeout(() => {
          if (video && !video.paused) {
            video.pause();
            logger.debug('TV video paused after fade out');
          }
          videoPauseTimeoutRef.current = null;
        }, TV_FADE_OUT_DURATION);

        logger.debug('TV video pause scheduled after fade out');
        if (hasVideoStarted) {
          setHasVideoStarted(false);
        }
      }
    }

    // Cleanup: clear timeout on unmount or when dependencies change
    return () => {
      if (videoPauseTimeoutRef.current) {
        clearTimeout(videoPauseTimeoutRef.current);
        videoPauseTimeoutRef.current = null;
      }
    };
  }, [isTvSceneActive, videoTexture.video, videoTexture.isVideoLoaded, hasVideoStarted]);

  // Detect when video has completed one full playthrough
  useEffect(() => {
    const video = videoTexture.video;
    if (!video || !videoSrc || hasVideoCompletedRef.current) return;

    // Remove existing listener if any
    if (videoEndListenerRef.current) {
      video.removeEventListener('timeupdate', videoEndListenerRef.current);
    }

    // Create listener to detect when video completes one loop
    const handleTimeUpdate = () => {
      if (!hasVideoPlayedRef.current || hasVideoCompletedRef.current || !video.duration) {
        previousVideoTimeRef.current = video.currentTime;
        return;
      }

      const currentTime = video.currentTime;
      const previousTime = previousVideoTimeRef.current;
      const duration = video.duration;

      // Detect completion: check if we're very close to the end (within 0.1 seconds)
      // or if the video has looped back (time went backwards significantly)
      const isNearEnd = currentTime >= duration - 0.1;
      const hasLooped = previousTime > duration * 0.9 && currentTime < 0.5;

      if ((isNearEnd || hasLooped) && !hasVideoCompletedRef.current) {
        hasVideoCompletedRef.current = true;
        logger.debug('TV video completed first playthrough, switching to static', {
          currentTime,
          previousTime,
          duration,
          isNearEnd,
          hasLooped
        });

        // Call the callback to switch to static mode using setTvChannel helper
        setTvChannel(null);

        // Remove the listener since we only need to detect the first completion
        video.removeEventListener('timeupdate', handleTimeUpdate);
        videoEndListenerRef.current = null;
      }

      previousVideoTimeRef.current = currentTime;
    };

    videoEndListenerRef.current = handleTimeUpdate;
    video.addEventListener('timeupdate', handleTimeUpdate);

    // Cleanup
    return () => {
      if (video && videoEndListenerRef.current) {
        video.removeEventListener('timeupdate', videoEndListenerRef.current);
        videoEndListenerRef.current = null;
      }
    };
  }, [videoTexture.video, videoSrc]);

  // Reset completion tracking when video source changes
  useEffect(() => {
    hasVideoCompletedRef.current = false;
    hasVideoPlayedRef.current = false;
    previousVideoTimeRef.current = 0;
  }, [videoSrc]);

  // Cleanup transition timeout on unmount
  useEffect(() => {
    return () => {
      if (channelTransitionTimeoutRef.current) {
        clearTimeout(channelTransitionTimeoutRef.current);
        channelTransitionTimeoutRef.current = null;
      }
    };
  }, []);

  // Show loading state
  if (isLoading) {
    return null; // Don't render anything while loading
  }

  if (error) {
    console.error('Error loading TV scene model:', error);
    return null;
  }

  return (
    <>
      {/* Render the TV scene */}
      {sceneObject && (
        <primitive
          ref={sceneRef}
          object={sceneObject}
          onPointerMove={(event) => {
            // Use helper function to raycast and find ALL objects under the cursor
            // (like click handler does) to find if any interactive element is under the cursor
            const debug = process.env.NODE_ENV === 'development';
            const foundInteractive = checkAllObjectsUnderCursor(
              event,
              sceneObject,
              isTvInteractiveElement,
              debug
            );

            // Set cursor based on whether we found an interactive element
            setCanvasPointer(foundInteractive);
          }}
          onClick={(event) => {
            // Stop event propagation to prevent bubbling
            event.stopPropagation();

            logger.debug('=== TV SCENE CLICKED ===');
            logger.debug('TV clicked at:', event.point);
            logger.debug('Clicked object:', event.object.name);
            logger.debug('Original name:', event.object.userData.originalName);
            logger.debug('Object type:', event.object.type);

            // Use helper function to get object hierarchy (same as used in checkAllObjectsUnderCursor)
            const namesToCheck = getObjectHierarchy(event.object, 5);

            logger.debug('All object names in hierarchy:', namesToCheck.map(n => `${n.name} (orig: ${n.origName})`));
            logger.debug('==========================');

            // Check for specific button clicks (case-insensitive, check all names in hierarchy)
            // Use the same helper functions as isTvInteractiveElement for consistency
            for (const { name, origName } of namesToCheck) {
              const normalizedName = name.toLowerCase();
              const normalizedOriginal = origName.toLowerCase();

              // Check for button 1 using helper function
              if (isTvButton1(normalizedName, normalizedOriginal)) {
                logger.debug('Button 1 clicked - changing channel');
                // Toggle between video and static using setTvChannel helper
                // Use quality-aware path when toggling back to video
                const newChannel = videoSrc ? null : qualityAwareVideoPath;
                setTvChannel(newChannel);
                return;
              }

              // Check for button 2 using helper function
              if (isTvButton2(normalizedName, normalizedOriginal)) {
                logger.debug('Button 2 clicked - toggling sound');
                if (onButton2Click) {
                  onButton2Click();
                }
                return;
              }

              // Check for screen-display using helper function
              if (isTvScreenDisplay(normalizedName, normalizedOriginal)) {
                logger.debug('Screen display clicked - zooming in');
                if (onSceneClick) {
                  onSceneClick();
                }
                return;
              }
            }

            // For any other object, don't do anything
            logger.debug('Clicked object is not screen-display or button - no action');
          }}
        />
      )}
    </>
  );
};

export default memo(TvScene, (prevProps, nextProps) => {
  // Only re-render if the callback functions or initial video source changed
  return prevProps.onSceneClick === nextProps.onSceneClick &&
    prevProps.onButton1Click === nextProps.onButton1Click &&
    prevProps.onButton2Click === nextProps.onButton2Click;
});
